import { createClient } from "@supabase/supabase-js"
import { SUPABASE_PUBLISHABLE_KEY, SUPABASE_URL } from "../config/supabase-public"

/**
 * The browser's Supabase client — MTC-FCS-008.
 * ============================================================================
 *
 * ── WHY THE PUBLISHABLE KEY IS IN THE BUNDLE ───────────────────────────────
 * It is meant to be. Supabase's publishable key identifies the project, not a
 * privileged caller; what it can actually read or write is decided by row
 * level security on the server. The SECRET key is a different thing entirely
 * and must never carry the GATSBY_ prefix, because that prefix is exactly what
 * puts a value into JavaScript every visitor downloads.
 *
 * ── ONE CLIENT, CREATED LAZILY ─────────────────────────────────────────────
 * `createClient` starts a session-refresh timer and touches localStorage, so
 * it cannot run during Gatsby's static render. Creating it on first use keeps
 * it out of the SSR pass and means a page that never asks about auth never
 * pays for it.
 *
 * ── IT REPORTS MISSING CONFIG RATHER THAN THROWING ─────────────────────────
 * `configured()` lets a screen say "accounts are not set up on this
 * environment" instead of exploding in a render. An auth form that white-
 * screens tells the visitor nothing and tells the developer almost as little.
 */

/* Committed, not read from the environment at build time — FCS-056. Both
   values are public, and a build without them is a site nobody can log in to;
   see the note in config/supabase-public.js. The environment still wins where
   it is set. */
const URL = SUPABASE_URL
const KEY = SUPABASE_PUBLISHABLE_KEY

export function configured() {
  return Boolean(URL && KEY)
}

export const NOT_CONFIGURED =
  "Accounts are not set up on this environment yet. Set the project URL and " +
  "publishable key in src/config/supabase-public.js to enable sign in."

let client = null

/** The shared client, or null when the project is not configured. */
export function supabase() {
  if (!configured()) return null
  if (typeof window === "undefined") return null
  if (!client) {
    client = createClient(URL, KEY, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        // The session lives in this browser. Nothing here reads a session out
        // of a URL fragment except the callback below.
        detectSessionInUrl: true,
      },
    })
  }
  return client
}

/**
 * Turns a Supabase error into something worth showing a person.
 *
 * Supabase's messages are written for developers and occasionally leak whether
 * an address exists — "User already registered" tells an attacker that address
 * has an account here. The known ones are replaced; anything unrecognised is
 * passed through rather than swallowed, because a blank error is worse than an
 * awkward one.
 */
export function humanError(error) {
  if (!error) return ""
  const raw = String(error.message || error)

  if (/invalid login credentials/i.test(raw)) {
    return "That email and password do not match an account."
  }
  if (/email not confirmed/i.test(raw)) {
    return "Please confirm your email address first — check your inbox for the link."
  }
  if (/already registered|already exists/i.test(raw)) {
    // Deliberately the same shape as the success message: whether an address
    // is registered is not something a signup form should confirm.
    return "If that address is not already registered, a confirmation email is on its way."
  }
  if (/password.*at least|weak password/i.test(raw)) {
    return "Please choose a password of at least 8 characters."
  }
  if (/email.*rate limit|over_email_send_rate_limit/i.test(raw)) {
    // Distinct from a login rate limit: this is the PROJECT's outbound email
    // quota, and "try again in a minute" would be wrong — Supabase's built-in
    // SMTP allows only a couple of messages an hour.
    return (
      "We could not send the confirmation email just now — our mail quota is full. " +
      "Please try again shortly, or get in touch and we will set the account up for you."
    )
  }
  if (/rate limit|too many/i.test(raw)) {
    return "Too many attempts. Please wait a minute and try again."
  }
  return raw
}
